/**
 * Mounted Folder Index Store
 *
 * In-memory (non-persisted) store for tracking scan results
 * and scanning status of mounted folders.
 */

import { create } from 'zustand';
import type { IndexedSqlFile } from '../types/scriptBrowserTypes';

type MountedFolderIndexState = {
  /** Indexed .sql files per folder ID */
  readonly index: Record<string, IndexedSqlFile[]>;
  /** Folder IDs currently being scanned */
  readonly scanningFolderIds: string[];
  /**
   * Per-folder counter bumped when on-disk contents change in a way the tree
   * can't otherwise detect (e.g. Refresh from DB writes a new `Data/`
   * subfolder). `MountedFolderTree`'s `DirectoryContents` subscribes to this
   * and re-fetches its directory listing whenever the value changes.
   */
  readonly treeNonce: Record<string, number>;

  readonly setFolderIndex: (folderId: string, files: IndexedSqlFile[]) => void;
  readonly clearFolderIndex: (folderId: string) => void;
  readonly clearAllIndexes: () => void;
  readonly addScanningFolder: (folderId: string) => void;
  readonly removeScanningFolder: (folderId: string) => void;
  readonly bumpFolderTreeNonce: (folderId: string) => void;
};

export const useMountedFolderIndexStore = create<MountedFolderIndexState>()(
  (set) => ({
    index: {},
    scanningFolderIds: [],
    treeNonce: {},

    setFolderIndex: (folderId, files) =>
      set((state) => ({
        index: { ...state.index, [folderId]: files },
      })),

    clearFolderIndex: (folderId) =>
      set((state) => {
        const { [folderId]: _, ...rest } = state.index;
        return { index: rest };
      }),

    clearAllIndexes: () => set({ index: {}, scanningFolderIds: [], treeNonce: {} }),

    addScanningFolder: (folderId) =>
      set((state) => ({
        scanningFolderIds: state.scanningFolderIds.includes(folderId)
          ? state.scanningFolderIds
          : [...state.scanningFolderIds, folderId],
      })),

    removeScanningFolder: (folderId) =>
      set((state) => ({
        scanningFolderIds: state.scanningFolderIds.filter((id) => id !== folderId),
      })),

    bumpFolderTreeNonce: (folderId) =>
      set((state) => ({
        treeNonce: {
          ...state.treeNonce,
          [folderId]: (state.treeNonce[folderId] ?? 0) + 1,
        },
      })),
  })
);
